import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Send, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Complete los campos requeridos"); return; }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Mensaje enviado correctamente. Nos pondremos en contacto pronto.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch {
      toast.error("Error al enviar. Intente de nuevo.");
    }
    setSubmitting(false);
  };

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <main data-testid="contact-page" className="pt-20 lg:pt-24 bg-construtop-texture">
      <section className="bg-zinc-950 py-20 lg:py-28 section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-emerald-400 text-sm font-bold uppercase tracking-[0.2em] mb-3">
            Contacto
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-4xl sm:text-5xl text-white mb-4">
            Hablemos de su proyecto
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-zinc-400 max-w-xl text-base">
            Nuestro equipo de ingenieros está listo para asesorarle. Complete el formulario o contáctenos directamente.
          </motion.p>
        </div>
      </section>

      <section className="section-spacing section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="lg:col-span-2">
              <motion.h3 variants={fadeUp} className="font-heading text-2xl text-zinc-900 mb-8">Información de contacto</motion.h3>

              <div className="space-y-6">
                {[
                  { icon: Phone, label: "Teléfono", value: "+593 999 307 185", href: "tel:+593999307185" },
                  { icon: Mail, label: "Email", value: "comercial@construtop.com.ec", href: "mailto:comercial@construtop.com.ec" },
                  { icon: MapPin, label: "Oficinas", value: "Quito, Ecuador", href: null },
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeUp} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                      <item.icon size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-zinc-500 uppercase tracking-wider">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-zinc-900 font-medium hover:text-emerald-600 transition-colors">{item.value}</a>
                      ) : (
                        <p className="text-zinc-900 font-medium">{item.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeUp} className="mt-10">
                <a
                  href="https://wa.me/593999307185?text=Hola%2C%20me%20interesa%20conocer%20m%C3%A1s%20sobre%20las%20soluciones%20de%20impermeabilizaci%C3%B3n%20de%20Construtop."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button data-testid="contact-whatsapp-btn" size="lg" className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-none px-8 font-bold tracking-wide w-full">
                    <MessageCircle size={18} className="mr-2" /> ESCRIBIR POR WHATSAPP
                  </Button>
                </a>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3 bg-white border border-zinc-200 p-8 lg:p-10"
            >
              <h3 className="font-heading text-2xl text-zinc-900 mb-6">Enviar mensaje</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="contact-name">Nombre *</Label>
                    <Input data-testid="contact-name-input" id="contact-name" value={form.name} onChange={handleChange("name")} placeholder="Su nombre completo" className="rounded-none mt-1.5" required />
                  </div>
                  <div>
                    <Label htmlFor="contact-email">Email *</Label>
                    <Input data-testid="contact-email-input" id="contact-email" type="email" value={form.email} onChange={handleChange("email")} placeholder="correo@empresa.com" className="rounded-none mt-1.5" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="contact-phone">Teléfono</Label>
                    <Input data-testid="contact-phone-input" id="contact-phone" value={form.phone} onChange={handleChange("phone")} placeholder="+593 999 000 000" className="rounded-none mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="contact-company">Empresa</Label>
                    <Input data-testid="contact-company-input" id="contact-company" value={form.company} onChange={handleChange("company")} placeholder="Nombre de su empresa" className="rounded-none mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="contact-subject">Asunto</Label>
                  <Input data-testid="contact-subject-input" id="contact-subject" value={form.subject} onChange={handleChange("subject")} placeholder="Tema de su consulta" className="rounded-none mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="contact-message">Mensaje *</Label>
                  <Textarea data-testid="contact-message-input" id="contact-message" value={form.message} onChange={handleChange("message")} placeholder="Describa su proyecto o consulta..." rows={5} className="rounded-none mt-1.5 resize-none" required />
                </div>
                <Button data-testid="contact-submit-btn" type="submit" disabled={submitting} size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold tracking-wide">
                  {submitting ? "ENVIANDO..." : "ENVIAR MENSAJE"} <Send size={16} className="ml-2" />
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
